/**
 * Feedback Context
 * Global context for feedback feature with element selection mode
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const FeedbackContext = createContext();

export const useFeedback = () => {
  const context = useContext(FeedbackContext);
  if (!context) {
    throw new Error('useFeedback must be used within FeedbackProvider');
  }
  return context;
};

export const FeedbackProvider = ({ children }) => {
  const [feedbackMode, setFeedbackMode] = useState(false);
  const [selectedElement, setSelectedElement] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectionMode, setSelectionMode] = useState(false); // Element selection mode active
  const [hoveredElement, setHoveredElement] = useState(null); // Currently hovered element
  const [showConfirmation, setShowConfirmation] = useState(false); // Show confirmation dialog
  const [confirmationPosition, setConfirmationPosition] = useState(null); // Position for confirmation dialog
  const [selectedElementForConfirmation, setSelectedElementForConfirmation] = useState(null); // Element to track for scroll

  /**
   * Start element selection mode
   */
  const startSelectionMode = useCallback(() => {
    setSelectionMode(true);
    setShowConfirmation(false);
    setHoveredElement(null);

    // Disable text selection while in selection mode
    document.body.style.userSelect = 'none';
    document.body.style.webkitUserSelect = 'none';
    document.body.style.cursor = 'crosshair';
  }, []);

  /**
   * Cancel element selection mode
   */
  const cancelSelectionMode = useCallback(() => {
    setSelectionMode(false);
    setShowConfirmation(false);
    setHoveredElement(null);
    setSelectedElementForConfirmation(null);

    // Re-enable text selection
    document.body.style.userSelect = '';
    document.body.style.webkitUserSelect = '';
    document.body.style.cursor = '';
  }, []);

  /**
   * Confirm element selection and open feedback panel
   */
  const confirmElementSelection = useCallback(() => {
    if (!hoveredElement) return;


    const elementPath = getElementPath(hoveredElement.element);
    const elementRect = getElementRect(hoveredElement.element);

    setFeedbackMode(true);
    setSelectedElement({
      element: hoveredElement.element,
      path: elementPath,
      rect: elementRect
    });
    setShowModal(true);
    setSelectionMode(false);
    setShowConfirmation(false);
    setHoveredElement(null);

    // Keep text selection disabled while feedback modal is open
    document.body.style.cursor = '';
  }, [hoveredElement]);

  /**
   * Reselect element (close confirmation, stay in selection mode)
   */
  const reselectElement = useCallback(() => {
    setShowConfirmation(false);
    setHoveredElement(null);
    setSelectedElementForConfirmation(null);
  }, []);

  /**
   * Disable feedback mode and clear selection
   */
  const disableFeedbackMode = useCallback(() => {

    setFeedbackMode(false);
    setSelectedElement(null);
    setShowModal(false);
    setSelectionMode(false);
    setShowConfirmation(false);
    setHoveredElement(null);
    setSelectedElementForConfirmation(null);

    // Re-enable text selection
    document.body.style.userSelect = '';
    document.body.style.webkitUserSelect = '';
    document.body.style.cursor = '';
  }, []);

  /**
   * Close modal (but keep element selected for highlighting)
   */
  const closeModal = useCallback(() => {
    setShowModal(false);

    // Fully disable feedback mode after modal closes
    setTimeout(() => {
      disableFeedbackMode();
    }, 300); // Wait for slide-out animation
  }, [disableFeedbackMode]);

  /**
   * Get element selector path
   */
  const getElementPath = useCallback((element) => {
    if (!element) return 'unknown';

    // If element has an ID, use that
    if (element.id) {
      return `#${element.id}`;
    }

    // Build path from element to root
    const path = [];
    let current = element;

    while (current && current.nodeType === Node.ELEMENT_NODE) {
      let selector = current.nodeName.toLowerCase();

      // Add classes if available
      if (current.className && typeof current.className === 'string') {
        const classes = current.className.trim().split(/\s+/).filter(c => c && !c.includes('hover') && !c.includes('focus'));
        if (classes.length > 0) {
          selector += '.' + classes.slice(0, 3).join('.'); // Max 3 classes
        }
      }

      // Add data attributes for better identification
      if (current.dataset && Object.keys(current.dataset).length > 0) {
        const dataKey = Object.keys(current.dataset)[0];
        selector += `[data-${dataKey}]`;
      }

      path.unshift(selector);
      current = current.parentNode;

      // Limit path depth to 5 levels
      if (path.length >= 5) break;
    }

    return path.join(' > ');
  }, []);

  /**
   * Get element bounding rectangle
   */
  const getElementRect = useCallback((element) => {
    if (!element) return null;
    const rect = element.getBoundingClientRect();
    return {
      top: rect.top,
      left: rect.left,
      width: rect.width,
      height: rect.height,
      bottom: rect.bottom,
      right: rect.right
    };
  }, []);

  /**
   * Calculate confirmation dialog position based on element
   */
  const calculateConfirmationPosition = useCallback((element) => {
    if (!element) return null;

    const rect = getElementRect(element);
    if (!rect) return null;

    // Dialog dimensions
    const dialogHeight = 120;
    const dialogWidth = 300;
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const padding = 20;

    let top = rect.bottom + 10;
    let left = rect.left + rect.width / 2;

    // If dialog would go below viewport, show it above the element
    if (top + dialogHeight > viewportHeight - padding) {
      top = rect.top - dialogHeight - 10;
    }

    // If still going off screen (element at top), center it vertically
    if (top < padding) {
      top = viewportHeight / 2 - dialogHeight / 2;
    }

    // Keep horizontal position in bounds
    const halfDialogWidth = dialogWidth / 2;
    if (left - halfDialogWidth < padding) {
      left = halfDialogWidth + padding;
    } else if (left + halfDialogWidth > viewportWidth - padding) {
      left = viewportWidth - halfDialogWidth - padding;
    }

    return { top, left };
  }, [getElementRect]);

  /**
   * Handle mouse move to highlight hovered element
   */
  const handleMouseMove = useCallback((event) => {
    if (!selectionMode || showConfirmation) return;

    const target = event.target;
    const tagName = target.tagName.toLowerCase();

    // Don't highlight certain elements
    if (
      tagName === 'body' ||
      tagName === 'html' ||
      target.closest('.feedback-button') ||
      target.closest('.feedback-confirmation') ||
      target.closest('.feedback-highlight')
    ) {
      setHoveredElement(null);
      return;
    }

    const rect = getElementRect(target);
    setHoveredElement({ element: target, rect });
  }, [selectionMode, showConfirmation, getElementRect]);

  /**
   * Handle click to select element
   */
  const handleClick = useCallback((event) => {
    if (!selectionMode) return;

    const target = event.target;
    const tagName = target.tagName.toLowerCase();

    // Ignore clicks on confirmation dialog
    if (target.closest('.feedback-confirmation')) {
      return;
    }

    // Ignore clicks on feedback button
    if (target.closest('.feedback-button')) {
      return;
    }

    // Don't select certain elements
    if (
      tagName === 'body' ||
      tagName === 'html'
    ) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    // Show confirmation dialog with smart positioning
    const rect = getElementRect(target);
    setHoveredElement({ element: target, rect });
    setSelectedElementForConfirmation(target);

    const position = calculateConfirmationPosition(target);
    setConfirmationPosition(position);
    setShowConfirmation(true);

  }, [selectionMode, getElementRect]);

  /**
   * Update confirmation position on scroll
   */
  useEffect(() => {
    if (showConfirmation && selectedElementForConfirmation) {
      const handleScroll = () => {
        const position = calculateConfirmationPosition(selectedElementForConfirmation);
        setConfirmationPosition(position);

        // Also update hovered element rect for highlight
        const rect = getElementRect(selectedElementForConfirmation);
        setHoveredElement({ element: selectedElementForConfirmation, rect });
      };

      // Listen to scroll on window and all scrollable parents
      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleScroll);

      return () => {
        window.removeEventListener('scroll', handleScroll, true);
        window.removeEventListener('resize', handleScroll);
      };
    }
  }, [showConfirmation, selectedElementForConfirmation, calculateConfirmationPosition, getElementRect]);

  /**
   * Set up event listeners for selection mode
   */
  useEffect(() => {
    if (selectionMode) {
      document.addEventListener('mousemove', handleMouseMove, true);
      document.addEventListener('click', handleClick, true);

      return () => {
        document.removeEventListener('mousemove', handleMouseMove, true);
        document.removeEventListener('click', handleClick, true);
      };
    }
  }, [selectionMode, handleMouseMove, handleClick]);

  const value = {
    feedbackMode,
    selectedElement,
    showModal,
    selectionMode,
    hoveredElement,
    showConfirmation,
    confirmationPosition,
    startSelectionMode,
    cancelSelectionMode,
    confirmElementSelection,
    reselectElement,
    disableFeedbackMode,
    closeModal
  };

  return (
    <FeedbackContext.Provider value={value}>
      {children}
    </FeedbackContext.Provider>
  );
};
